export const CALENDAR_TIMEZONE = 'America/Chicago';

export function calendarToday(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: CALENDAR_TIMEZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
  const part = type => parts.find(item => item.type === type).value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function eventStatus(event, today = calendarToday()) {
  if (event.cancelled) return 'cancelled';
  if (event.dateStatus !== 'confirmed' || !event.startDate || !event.endDate) return 'unconfirmed';
  if (event.endDate < today) return 'completed';
  if (event.startDate <= today) return 'ongoing';
  return 'upcoming';
}

export function formatEventDates(event) {
  if (event.dateStatus !== 'confirmed' || !event.startDate || !event.endDate) return '2026–27 date to be announced';
  const format = value => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}T12:00:00Z`));
  return event.startDate === event.endDate ? format(event.startDate) : `${format(event.startDate)} – ${format(event.endDate)}`;
}

// Split inclusive date ranges at week/month boundaries for a real seven-day calendar.
export function buildMonthWeeks(events, year, month) {
  const iso = date => date.toISOString().slice(0, 10);
  const first = new Date(Date.UTC(year, month, 1));
  const start = new Date(first); start.setUTCDate(1 - first.getUTCDay());
  const weeks = [];
  for (let index = 0; index < 6; index += 1) {
    const dates = Array.from({ length: 7 }, (_, day) => {
      const date = new Date(start); date.setUTCDate(start.getUTCDate() + index * 7 + day);
      return { date: iso(date), number: date.getUTCDate(), inMonth: date.getUTCMonth() === month };
    });
    const visible = dates.filter(day => day.inMonth);
    const segments = visible.length ? events.filter(event => event.dateStatus === 'confirmed' && !event.cancelled && event.startDate <= visible[visible.length - 1].date && event.endDate >= visible[0].date).map(event => {
      const from = dates.findIndex(day => day.inMonth && day.date >= event.startDate && day.date <= event.endDate);
      let to = from;
      while (to < 6 && dates[to + 1].inMonth && dates[to + 1].date <= event.endDate) to += 1;
      return { event, column: from + 1, span: to - from + 1 };
    }).sort((a, b) => a.column - b.column || b.span - a.span || a.event.name.localeCompare(b.event.name)) : [];
    const lanes = [];
    segments.forEach(segment => {
      let lane = lanes.findIndex(occupied => occupied.every(other => segment.column + segment.span <= other.column || other.column + other.span <= segment.column));
      if (lane < 0) { lane = lanes.length; lanes.push([]); }
      lanes[lane].push(segment); segment.lane = lane;
    });
    weeks.push({ dates, segments, laneCount: lanes.length });
  }
  return weeks;
}

export function filterEvents(events, { query = '', region = '', status = 'all', type = '' } = {}, today = calendarToday()) {
  const search = query.trim().toLowerCase();
  const order = { ongoing: 0, upcoming: 1, unconfirmed: 2, completed: 3, cancelled: 4 };
  return events.filter(event => {
    const current = eventStatus(event, today);
    return (!search || [event.name, event.city, event.region, event.venue, event.country, event.notes].filter(Boolean).join(' ').toLowerCase().includes(search))
      && (!region || event.region === region)
      && (!type || event.type === type)
      && (status === 'all' || (status === 'upcoming' ? ['upcoming', 'ongoing'].includes(current) : status === current));
  }).sort((a, b) => {
    const group = order[eventStatus(a, today)] - order[eventStatus(b, today)];
    if (group) return group;
    if (eventStatus(a, today) === 'completed') return (b.endDate || '').localeCompare(a.endDate || '');
    return (a.startDate || '9999').localeCompare(b.startDate || '9999') || a.name.localeCompare(b.name);
  });
}

const icalEscape = value => String(value || '').replace(/\\/g, '\\\\').replace(/\r?\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
const dateValue = date => date.replace(/-/g, '');
function exclusiveEnd(date) {
  const next = new Date(`${date}T12:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
}

export function buildCalendarIcs(events, now = new Date()) {
  const today = calendarToday(now);
  const stamp = now.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const upcoming = events.filter(event => ['ongoing', 'upcoming'].includes(eventStatus(event, today)));
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Angler Built//Ice Show Calendar//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', 'X-WR-CALNAME:Angler Built Ice Shows 2026–27'];
  upcoming.forEach(event => lines.push('BEGIN:VEVENT', `UID:${icalEscape(event.id)}@anglerbuilt.com`, `DTSTAMP:${stamp}`, `DTSTART;VALUE=DATE:${dateValue(event.startDate)}`, `DTEND;VALUE=DATE:${dateValue(exclusiveEnd(event.endDate))}`, `SUMMARY:${icalEscape(event.name)}`, `LOCATION:${icalEscape([event.venue, event.city, event.region, event.country].filter(Boolean).join(', '))}`, `DESCRIPTION:${icalEscape([event.notes, 'All-day dates only. Confirm hours and ticket details with the organizer.', event.sourceUrl].filter(Boolean).join('\n'))}`, `URL:${event.sourceUrl}`, 'END:VEVENT'));
  lines.push('END:VCALENDAR');
  // Fold UTF-8 content at a conservative byte boundary for calendar clients.
  return lines.flatMap(line => {
    const chunks = []; let chunk = ''; let bytes = 0;
    for (const character of line) {
      const size = new TextEncoder().encode(character).length;
      if (bytes + size > 72) { chunks.push(chunk); chunk = ' '; bytes = 1; }
      chunk += character; bytes += size;
    }
    chunks.push(chunk); return chunks;
  }).join('\r\n') + '\r\n';
}
